/**
 * Single source of truth for tier metadata — prices, blurbs,
 * feature bullets, ordering. Imported by:
 *   - `routes/Plans.tsx` (pricing cards)
 *   - `routes/Billing.tsx` (tier label on KPI tile + payment row)
 *   - `components/ConfirmPlanChangeModal.tsx` (price string in CTA)
 *
 * The wording (blurbs + features) is copied verbatim from the
 * design folder's `plans.html` so it stays in sync with what
 * marketing approved.
 *
 * Pricing is hardcoded because the Razorpay backend has no
 * pricing endpoint (the dashboard is the source of truth for the
 * actual charge amount). If you bump a price here, also bump it
 * in the Razorpay LIVE plan + the webhook fallback in
 * `functions/src/billing/razorpay/webhook.ts`.
 *
 * Pure data + helpers. No hooks, no Firebase imports. Safe to
 * import from anywhere.
 */

export type Tier = 'free' | 'solo' | 'studio' | 'studio_plus' | 'agency';
export type PaidTier = Exclude<Tier, 'free'>;

/** Smallest → largest. Drives `compareTiers` upgrade/downgrade
 *  detection. MUST match `functions/src/billing/limits.ts::PLAN_LIMITS`. */
export const TIER_ORDER: readonly Tier[] = [
  'free',
  'solo',
  'studio',
  'studio_plus',
  'agency',
] as const;

export const PAID_TIERS: readonly PaidTier[] = [
  'solo',
  'studio',
  'studio_plus',
  'agency',
] as const;

export type BillingPlan = {
  tier: Tier;
  /** Display name shown in the card title + modal copy. */
  label: string;
  /** Short tagline under the title (max ~50 chars). */
  blurb: string;
  /** Numeric monthly price in rupees, or `null` for the free
   *  plan. Used to compute paise math + render the price label. */
  priceInr: number | null;
  /** Pre-formatted price string ("₹599" / "Free") — single
   *  formatting site so Billing/Plans/Modal stay consistent. */
  priceLabel: string;
  /** Cadence suffix shown next to the price ("/ month" / "forever"). */
  cadence: string;
  /** Tiny line under the price ("Auto-renews monthly · + GST"). */
  meta: string;
  /** Feature bullets. The first string may be "Everything in X,
   *  plus:" which is rendered as a section heading (no check
   *  icon) — convention: ends with ":". */
  features: string[];
  /** Mark POPULAR / highlight border (Studio only). */
  highlight?: boolean;
};

export const BILLING_PLANS: readonly BillingPlan[] = [
  {
    tier: 'free',
    label: 'Free',
    blurb: 'Try the app — 1 user, 1 project',
    priceInr: null,
    priceLabel: 'Free',
    cadence: 'forever',
    meta: 'No card required · Get started in 60s',
    features: [
      '1 active project',
      '1 team member',
      'Daily progress reports',
      'Task tracking',
      'Materials & parties library',
    ],
  },
  {
    tier: 'solo',
    label: 'Solo',
    blurb: 'For solo designers — unlimited projects',
    priceInr: 599,
    priceLabel: '₹599',
    cadence: '/ month',
    meta: 'Auto-renews monthly · + GST',
    features: [
      'Unlimited projects',
      '1 team member',
      'Daily progress reports + photos',
      'Task tracking with progress updates',
      'Materials & parties library',
      'Email support',
    ],
  },
  {
    tier: 'studio',
    label: 'Studio',
    blurb: 'For small studios — up to 6 team members',
    priceInr: 999,
    priceLabel: '₹999',
    cadence: '/ month',
    meta: 'Auto-renews monthly · + GST',
    features: [
      'Everything in Solo, plus:',
      'Up to 6 team members',
      'Approval workflow for transactions',
      'Material requests across the team',
      'Client view with shared updates',
      'Onboarding session with our team',
    ],
    highlight: true,
  },
  {
    tier: 'studio_plus',
    label: 'Studio+',
    blurb: 'For growing studios — up to 15 team members',
    priceInr: 1999,
    priceLabel: '₹1,999',
    cadence: '/ month',
    meta: 'Auto-renews monthly · + GST',
    features: [
      'Everything in Studio, plus:',
      'Up to 15 team members',
      'Advanced project analytics',
      'Custom roles and permissions',
      'Priority support (within 4 hours)',
      'Quarterly success check-ins',
    ],
  },
  {
    tier: 'agency',
    label: 'Agency',
    blurb: 'For larger studios — unlimited team',
    priceInr: 3999,
    priceLabel: '₹3,999',
    cadence: '/ month',
    meta: 'Auto-renews monthly · + GST',
    features: [
      'Everything in Studio+, plus:',
      'Unlimited team members',
      'Custom integrations on request',
      'Dedicated success manager',
      'SLA with priority response',
      'Tailored studio onboarding',
    ],
  },
] as const;

// ───── Helpers ──────────────────────────────────────────────

/** Lookup helper. Falls back to the Free plan on unknown tier
 *  (safer than throwing — keeps the UI rendering even if the
 *  server writes a value we don't recognise). */
export function planForTier(t: Tier | string | undefined): BillingPlan {
  const plan = BILLING_PLANS.find((p) => p.tier === t);
  return plan ?? BILLING_PLANS[0];
}

/** Pretty tier name used in modal copy + banners. */
export function tierLabel(t: Tier | string | undefined): string {
  return planForTier(t as Tier).label;
}

/** Returns -1 if a < b, 0 if equal, 1 if a > b. Unknown tiers
 *  treated as 'free' to fail safely. */
export function compareTiers(
  a: Tier | string | undefined,
  b: Tier | string | undefined,
): -1 | 0 | 1 {
  const ai = TIER_ORDER.indexOf(a as Tier);
  const bi = TIER_ORDER.indexOf(b as Tier);
  const aSafe = ai >= 0 ? ai : 0;
  const bSafe = bi >= 0 ? bi : 0;
  if (aSafe < bSafe) return -1;
  if (aSafe > bSafe) return 1;
  return 0;
}

export function isPaidTier(t: Tier | string | undefined): t is PaidTier {
  return (
    t === 'solo' || t === 'studio' || t === 'studio_plus' || t === 'agency'
  );
}
